import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import api from '../services/api';
import { setUser as setSentryUser } from '../config/sentry';
import { setCurrentUser as setGoogleMapsLoggerUser } from '../services/googleMapsLogger';
import { prefetchCommonData } from '../utils/prefetch';

const AuthContext = createContext();

// Session storage key for user cache
const USER_CACHE_KEY = 'vibary_user_cache';

// Get cached user from sessionStorage
const getCachedUser = () => {
  try {
    const cached = sessionStorage.getItem(USER_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // Ignore errors
  }
  return null;
};

// Save user to sessionStorage cache
const setCachedUser = (userData) => {
  try {
    if (userData) {
      sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    } else {
      sessionStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {
    // Ignore errors (e.g., storage full)
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize from cache for instant render on page refresh
  const cachedUser = getCachedUser();
  const [user, setUser] = useState(cachedUser);
  // If we have cached user, don't show loading state - render immediately
  const [loading, setLoading] = useState(!cachedUser);
  // Track whether Firebase auth has completed its initial check (for API calls that need token)
  const [authReady, setAuthReady] = useState(false);
  const signupInProgressRef = useRef(false);
  const loginInProgressRef = useRef(false);
  const loginResolveRef = useRef(null);

  // Wrapper to update user and cache
  const setUserWithCache = (userData) => {
    setUser(userData);
    setCachedUser(userData);
  };

  useEffect(() => {
    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Skip if signup is in progress - signup will handle setting the user
      if (signupInProgressRef.current) {
        return;
      }

      // Firebase has resolved the session, so ID tokens can be minted from
      // here on. Unblock token-gated consumers (useListDetail, PlaceDetails)
      // now rather than after the profile fetch below.
      setAuthReady(true);

      if (firebaseUser) {
        // User is signed in with Firebase
        // If we have cached user with matching UID, use it immediately and revalidate in background
        // User docs are keyed by Firebase UID, exposed as `id` in API responses
        const cachedUserData = getCachedUser();
        const cachedUid = cachedUserData?.firebaseUid || cachedUserData?.id;
        if (cachedUserData && cachedUid === firebaseUser.uid) {
          // Already set from initial state, just ensure logging contexts are set
          setSentryUser(firebaseUser);
          setGoogleMapsLoggerUser(cachedUserData);
          setLoading(false);

          // Revalidate in background (stale-while-revalidate pattern)
          api.getUser(firebaseUser.uid).then((response) => {
            setUserWithCache(response.data);
            setGoogleMapsLoggerUser(response.data);
          }).catch(() => {
            // Silently fail - cached data is still valid
          });

          // Prefetch common API data in background
          firebaseUser.getIdToken().then((token) => {
            prefetchCommonData(cachedUserData, token);
          }).catch(() => {});

          // If login is in progress, resolve the promise
          if (loginInProgressRef.current && loginResolveRef.current) {
            loginResolveRef.current({ success: true });
            loginInProgressRef.current = false;
            loginResolveRef.current = null;
          }
          return;
        }

        // No cache or UID mismatch - fetch from API
        try {
          const response = await api.getUser(firebaseUser.uid);
          setUserWithCache(response.data);
          // Set user context for Sentry error tracking
          setSentryUser(firebaseUser);
          // Set user context for Google Maps API logging
          setGoogleMapsLoggerUser(response.data);

          // Prefetch common API data in background after auth
          firebaseUser.getIdToken().then((token) => {
            prefetchCommonData(response.data, token);
          }).catch(() => {});

          // If login is in progress, resolve the promise
          if (loginInProgressRef.current && loginResolveRef.current) {
            loginResolveRef.current({ success: true });
            loginInProgressRef.current = false;
            loginResolveRef.current = null;
          }
        } catch (error) {
          // User profile doesn't exist in database
          console.error('Error fetching user data:', error);
          setUserWithCache(null);

          // If login is in progress, resolve with error
          if (loginInProgressRef.current && loginResolveRef.current) {
            loginResolveRef.current({ success: false, error: 'Failed to load user data' });
            loginInProgressRef.current = false;
            loginResolveRef.current = null;
          }
        }
      } else {
        // User is signed out
        setUserWithCache(null);
        setSentryUser(null);
        setGoogleMapsLoggerUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    if (!email || !password) {
      return { success: false, error: 'Email and password are required' };
    }

    try {
      // Set up promise to wait for user data to be loaded
      loginInProgressRef.current = true;
      const userDataPromise = new Promise((resolve) => {
        loginResolveRef.current = resolve;
      });

      // Sign in with Firebase
      await signInWithEmailAndPassword(auth, email, password);

      // Wait for onAuthStateChanged to finish loading user data
      const result = await userDataPromise;
      return result;
    } catch (error) {
      loginInProgressRef.current = false;
      loginResolveRef.current = null;

      let errorMessage = 'Login failed';

      // Provide user-friendly error messages
      if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later';
      }

      return { success: false, error: errorMessage };
    }
  };

  const signup = async (email, password, username, displayName) => {
    if (!email || !password || !username || !displayName) {
      return { success: false, error: 'All fields are required' };
    }

    // Set flag to prevent onAuthStateChanged from interfering
    signupInProgressRef.current = true;

    try {
      // Create user with Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUid = userCredential.user.uid;

      // Create user profile in database with Firebase UID
      const response = await api.createUser({
        firebaseUid,
        username,
        displayName,
        email,
      });

      // Set user directly from the response and cache it
      setUserWithCache(response.data);
      // Set user context for Google Maps API logging
      setGoogleMapsLoggerUser(response.data);
      setLoading(false);
      signupInProgressRef.current = false;

      return { success: true };
    } catch (error) {
      signupInProgressRef.current = false;
      let errorMessage = 'Signup failed';

      // Provide user-friendly error messages
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.message) {
        // Backend validation errors
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserWithCache(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Update user data in context (for profile updates without re-fetching)
  const updateUserData = (updatedData) => {
    setUser(prev => {
      const updated = prev ? { ...prev, ...updatedData } : null;
      setCachedUser(updated);
      return updated;
    });
  };

  // Refresh user data from the server
  const refreshUser = async () => {
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      try {
        const response = await api.getUser(firebaseUser.uid);
        setUserWithCache(response.data);
        return response.data;
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
    return null;
  };

  const value = {
    user,
    isAdmin: user?.isAdmin || false,
    login,
    signup,
    logout,
    loading,
    authReady,
    updateUserData,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
